import { Component, Injector, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ConfirmModalComponent } from '../../../common/components/confirm-modal/confirm-modal.component';
import { CurrencyCollection, FuelPreferenceCollection, RequestedDocument, UnitsCollection } from '../../../common/models';
import { Location } from '../../../geodata/models';
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import { Contract, Tender } from '../../models';
import { TenderBidsService, TenderContractsService, TenderService } from '../../services';
import { ContractFormModalComponent } from './components/contract-form-modal/contract-form-modal.component';
import { ContractsImportModalComponent } from './components/contracts-import-modal/contracts-import-modal.component';
import { ContractsImportTableModalComponent } from './components/contracts-import-table-modal/contracts-import-table-modal.component';
import { ProductServiceModalComponent } from "./components/product-service-modal/product-service-modal.component";

@Component({
    selector: 'jft-tender-contracts',
    templateUrl: './create-tender-contracts.component.html',
    styleUrls: ['./create-tender-contracts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateTenderContractsComponent implements OnInit, OnDestroy {
    public tender: Tender;
    public contracts: Contract[] = [];
    public reason = 'At least one contract should be added.';

    public currenciesCollection: CurrencyCollection;
    public unitsCollection: UnitsCollection;
    public fuelPreferenceCollection: FuelPreferenceCollection;
    public locations: Location[];
    public requestedDocuments: RequestedDocument[] = [];

    public modalReference: any;
    public deletingAll = false;
    public loading: boolean = false;

    // Pagination properties
    public currentPage: number = 1;
    public pageSize: number = 30;
    public totalItems: number = 0;

    // Search and sorting properties
    public searchTerm: string = '';
    public sortField: string = 'location';
    public sortDirection: 'ASC' | 'DESC' = 'ASC';
    private searchSubject = new Subject<string>();

    @ViewChild('deleteAllPopover', { static: false }) deleteAllPopover: NgbPopover;

    constructor(
        private contractService: TenderContractsService,
        private contractsService: TenderBidsService,
        private tenderService: TenderService,
        private route: ActivatedRoute,
        private router: Router,
        private toast: ToastService,
        private injector: Injector,
        private ngbModal: NgbModal,
        private loadingIndicator: LoadingIndicatorService,
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.snapshot.data;

        this.tender = parentData['tender'];
        this.currenciesCollection = data['currenciesCollection'];
        this.unitsCollection = data['unitsCollection'];
        this.requestedDocuments = data['requestedDocuments'];
        this.fuelPreferenceCollection = data['fuelPreferenceCollection'];

        // Set up debounced search
        this.searchSubject.pipe(
            debounceTime(400), // Wait 400ms after user stops typing
            distinctUntilChanged() // Only emit if value has changed
        ).subscribe(searchTerm => {
            this.performSearch(searchTerm);
        });

        // Set default sort by location
        this.contractsService.setOrder(this.sortField, this.sortDirection);
        this.onPageChange(1);
    }

    public ngOnDestroy() {
        this.searchSubject.complete();
    }

    public openImportContact() {
        this.ngbModal.open(ContractsImportModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        }).result.then((data) => {
            this.refreshContracts();
        }, (error) => {
            // Modal dismissed, no action needed
        });
    }

    public openImportTableContact() {
        this.ngbModal.open(ContractsImportTableModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true,
            windowClass: 'contract-import-table'
        }).result.then((data) => {
            this.refreshContracts();
        }, (error) => {
        });
    }

    private refreshContracts() {
        this.onPageChange(this.currentPage);
    }

    public addContract() {
        let contract = Contract.create({
            date_start: this.tender.dateContractStart.toISOString(),
            date_end: this.tender.dateContractEnd.toISOString(),
            units: this.unitsCollection.getById(this.tender.unitsId).toJSON(),
            currency: this.currenciesCollection.getById(this.tender.currencyId).toJSON(),
            fuel_preference: this.fuelPreferenceCollection.getById(1) //into plane
        });

        this.openContractModal(contract);
    }

    public editContract(contract: Contract) {
        this.openContractModal(contract);
    }

    private openContractModal(contract: Contract) {
        this.modalReference = this.ngbModal.open(ContractFormModalComponent, {
            injector: Injector.create([
                { provide: Contract, useValue: contract },
                { provide: Tender, useValue: this.tender },
                { provide: CurrencyCollection, useValue: this.currenciesCollection },
                { provide: UnitsCollection, useValue: this.unitsCollection },
                { provide: FuelPreferenceCollection, useValue: this.fuelPreferenceCollection }
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        }).result.then((contract: Contract) => {
            this.refreshContracts();
        }, (error) => {
            // Modal dismissed, no action needed
        });
    }

    public deleteContract(contract: Contract) {
        this.contractService.delete(this.tender, contract)
            .subscribe((data) => {
                let index = this.contracts.indexOf(contract);
                this.contracts.splice(index, 1);
                this.toast.success(`Location removed`);
            }, (e) => {
                this.toast.error(e);
            });
    }

    public cancelDeleteAll() {
        if (this.deleteAllPopover) {
            this.deleteAllPopover.close();
        }
    }

    public deleteAllLocations() {
        this.deletingAll = true;

        // Fetch ALL contracts from the tender (not just the current page)
        this.contractService.fetch(this.tender.id).subscribe(
            (allContracts: Contract[]) => {
                if (allContracts.length === 0) {
                    this.deletingAll = false;
                    if (this.deleteAllPopover) {
                        this.deleteAllPopover.close();
                    }
                    this.toast.info('No locations to delete');
                    return;
                }

                const totalContracts = allContracts.length;
                let deletedCount = 0;
                let errorCount = 0;

                // Delete each contract
                allContracts.forEach((contract) => {
                    this.contractService.delete(this.tender, contract)
                        .subscribe(
                            (data) => {
                                deletedCount++;

                                // Check if this is the last deletion
                                if (deletedCount + errorCount === totalContracts) {
                                    this.deletingAll = false;
                                    if (this.deleteAllPopover) {
                                        this.deleteAllPopover.close();
                                    }

                                    if (errorCount === 0) {
                                        this.toast.success(`All ${totalContracts} locations deleted successfully`);
                                    } else {
                                        this.toast.warning(`${deletedCount} locations deleted, ${errorCount} failed`);
                                    }

                                    // Refresh the page to show empty state
                                    this.onPageChange(1);
                                }
                            },
                            (error) => {
                                errorCount++;
                                console.error('Error deleting contract:', error);

                                // Check if this is the last deletion attempt
                                if (deletedCount + errorCount === totalContracts) {
                                    this.deletingAll = false;
                                    if (this.deleteAllPopover) {
                                        this.deleteAllPopover.close();
                                    }

                                    if (deletedCount === 0) {
                                        this.toast.error('Failed to delete locations');
                                    } else {
                                        this.toast.warning(`${deletedCount} locations deleted, ${errorCount} failed`);
                                    }

                                    // Refresh the page even if there were errors
                                    this.onPageChange(1);
                                }
                            }
                        );
                });
            },
            (error) => {
                this.deletingAll = false;
                if (this.deleteAllPopover) {
                    this.deleteAllPopover.close();
                }
                this.toast.error('Failed to fetch locations for deletion');
                console.error('Error fetching contracts:', error);
            }
        );
    }

    public stepBack() {
        this.tenderService.stepBack(this.tender).subscribe((tender: Tender) => {
            this.tender = tender;
            this.router.navigateByUrl(`/tenders/new/${tender.id}/details`);
        }, (e) => {
            this.toast.error(e);
        });
    }

    public submitContracts() {
        this.tenderService.submitContracts(this.tender).subscribe(() => {
            this.toast.success('Tender updated');
            this.router.navigate(['tenders', 'new', this.tender.id, 'suppliers']);
        }, (e) => {
            this.toast.error(e);
        });
    }

    public deleteTender(): void {
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Delete Tender';
        modalRef.componentInstance.text = 'Are you sure you want to delete this tender draft? This action cannot be undone.';

        modalRef.result.then((result) => {
            if (result) {
                this.tenderService.delete(this.tender).subscribe({
                    next: () => {
                        this.toast.success('Tender deleted successfully');
                        this.router.navigate(['/tenders']);
                    },
                    error: (e) => {
                        this.toast.error(e);
                    }
                });
            }
        }, () => { });
    }

    public productServicesList(contract: Contract) {
        this.modalReference = this.ngbModal.open(ProductServiceModalComponent, {
            injector: Injector.create([
                { provide: Contract, useValue: contract },
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            centered: true
        }).result.then(() => {
            //
        }, (error) => {

        });
    }

    public getColspan(): number {
        let baseColumns = 6; // Location, Company, Delivery Point, Total Volume, Tags, Settings, Edit, Delete

        // Add columns that are not hidden
        if (!this.tender.company.settings.isHiddenField('location_fuel_type')) {
            baseColumns++;
        }
        if (!this.tender.company.settings.isHiddenField('location_flight_type')) {
            baseColumns++;
        }
        if (!this.tender.company.settings.isHiddenField('location_months_of_service')) {
            baseColumns++;
        }
        if (!this.tender.company.settings.isHiddenField('location_award_quota')) {
            baseColumns++;
        }

        return baseColumns;
    }

    // Pagination methods
    public onPageChange(page: number) {
        this.currentPage = page;
        this.contractsService.setPage(page);
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
            if (Array.isArray(result)) {
                this.contracts = result;
                this.totalItems = result.length;
                this.currentPage = 1;
            } else {
                this.contracts = result.contracts;
                this.totalItems = result.totalItems;
                this.currentPage = result.currentPage;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    public onPageSizeChange(pageSize: number) {
        this.pageSize = pageSize;
        this.currentPage = 1; // Reset to first page
        this.contractsService.setPageSize(pageSize).setPage(1);
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
            if (Array.isArray(result)) {
                this.contracts = result;
                this.totalItems = result.length;
                this.currentPage = 1;
            } else {
                this.contracts = result.contracts;
                this.totalItems = result.totalItems;
                this.currentPage = result.currentPage;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    // Helper methods for pagination display
    public get startIndex(): number {
        return (this.currentPage - 1) * this.pageSize + 1;
    }

    public get endIndex(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalItems);
    }

    // Search and sort methods
    public onSearchChange(searchTerm: string) {
        // Emit the search term to the subject, which will debounce it
        this.searchSubject.next(searchTerm);
    }

    private performSearch(searchTerm: string) {
        this.currentPage = 1; // Reset to first page when searching
        this.contractsService.setFilter(searchTerm);
        this.contractsService.setPage(1);
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
            if (Array.isArray(result)) {
                this.contracts = result;
                this.totalItems = result.length;
                this.currentPage = 1;
            } else {
                this.contracts = result.contracts;
                this.totalItems = result.totalItems;
                this.currentPage = result.currentPage;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, () => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error('Failed to search locations');
        });
    }

    public onSortChange(field: string) {
        // Toggle direction if clicking the same field, otherwise default to ASC
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.sortField = field;
            this.sortDirection = 'ASC';
        }

        this.currentPage = 1; // Reset to first page when sorting
        this.contractsService.setOrder(this.sortField, this.sortDirection);
        this.contractsService.setPage(1);
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchPaginated(this.tender.id).subscribe((result) => {
            if (Array.isArray(result)) {
                this.contracts = result;
                this.totalItems = result.length;
                this.currentPage = 1;
            } else {
                this.contracts = result.contracts;
                this.totalItems = result.totalItems;
                this.currentPage = result.currentPage;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, () => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error('Failed to sort locations');
        });
    }

    public getSortIcon(field: string): string {
        if (this.sortField !== field) {
            return 'lucideArrowUpDown';
        }
        return this.sortDirection === 'ASC' ? 'lucideArrowUp' : 'lucideArrowDown';
    }
}
